/*
 * CRITICAL FIX: URL Sort Parameter Infinite Loop Resolution
 * See: docs/AuraTable-URLSort-Fix.md for complete documentation
 * DO NOT REMOVE OR MODIFY THESE CHANGES WITHOUT READING THE DOCS
 */

import Table from '@mui/material/Table';
import { useLocation, useNavigate } from 'react-router';
import PropTypes from 'prop-types';
import Toolbar from '@mui/material/Toolbar';
import Button from '@mui/material/Button';
import { makeStyles } from '@mui/styles';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Paper from '@mui/material/Paper';
import {
  useFilters,
  useGlobalFilter,
  usePagination,
  useRowSelect,
  useSortBy,
  useTable,
} from 'react-table';
import { useDispatch } from 'react-redux';
import clsx from 'clsx';
import { useEffect, useRef } from 'react';
import AuraTableTopToolBar from 'app/main/apps/common/AuraTableTopToolBar';
import { setResetFilters } from '../e-commerce/store/ordersSlice';
import AuraTablePaginationActions from './AuraTablePaginationActions';
import ListInfoMessage, { Loader } from './ListInfoMessage';

/** react-table props include `key`; React 19 requires key on JSX directly, not via spread */
function splitTableProps(props) {
  if (!props) {
    return { key: undefined, rest: {} };
  }
  const { key, ...rest } = props;
  return { key, rest };
}

const useToolbarStyles = makeStyles((theme) => ({
  root: {
    minHeight: '45px',
    paddingLeft: '0px',
    color: theme.palette.text.primary,
  },
  colorBox: {},
}));

const AuraTable = ({
  columns,
  data,
  updateMyData,
  AddItemToCart,
  onRowClick,
  skipPageReset = false,
  resetFilters,
  cellData = null,
  toolbarParam = null,
  showPagination = true,
  elevation = 1,
  includeAllOptions = true,
  setCartObj,
  serverPaginationProps,
  headers,
  params,
  updateParams,
  onSorting,
  loaderProps,
  loading = false,
  showListInfo = false,
  loaderWithSpace = false,
  noDataMessage,
  resetFunction = null,
  onParentFilterReset = null
}) => {
  const {
    getTableProps,
    headerGroups,
    rows,
    prepareRow,
    page,
    gotoPage,
    setPageSize,
    selectedFlatRows,
    toggleAllRowsSelected,
    state: { pageIndex, pageSize, selectedRowIds, sortBy },
    setGlobalFilter,
    setAllFilters,
    setSortBy,
  } = useTable(
    {
      columns,
      data,
      autoResetPage: !skipPageReset,
      autoResetExpanded: !skipPageReset,
      autoResetGroupBy: !skipPageReset,
      autoResetSelectedRows: !skipPageReset,
      autoResetSortBy: !skipPageReset,
      autoResetFilters: !skipPageReset,
      autoResetRowState: !skipPageReset,
      manualPagination: Boolean(serverPaginationProps),
      manualSortBy: Boolean(serverPaginationProps),
      // updateMyData, AddItemToCart aren't part of the API, but
      // anything we put into these options will
      // automatically be available on the instance.
      // That way we can call this function from our
      // cell renderer!
      updateMyData,
      AddItemToCart,
      setCartObj,
      cellData,
      params,
      updateParams
    },
    useFilters,
    useGlobalFilter,
    useSortBy,
    usePagination,
    useRowSelect,
  );
  const ref = useRef()

  // FIX 1: Track user interaction to prevent URL sort conflicts
  // This prevents URL sort arrows from showing after user manually sorts
  // Solves: Dual arrows showing when URL has sort + user manually sorts different column
  const hasUserInteracted = useRef(false);

  const location = useLocation();
  const classes = useToolbarStyles();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  let count = rows.length;
  let rowsPerPage = pageSize;
  let currentPage = pageIndex;
  // ServerPaginationProps indicates we are using serverside pagination and not using local
  if (serverPaginationProps) {
    count = Number(headers['app-pagination-total-records'] || 0);
    rowsPerPage = Number(headers['app-per-page-limit'] || pageSize);
    currentPage = Number(headers['app-pagination-current-page-num'] || 0)
  }
  const handleChangePage = (event, newPage) => {
    if (serverPaginationProps)
      return serverPaginationProps.changePage(newPage)
    return gotoPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    if (serverPaginationProps) {
      return serverPaginationProps.perPage(Number(event.target.value))
    }
    return setPageSize(Number(event.target.value));
  };

  // useEffect(() => {
  //   if (resetFilters === true) setAllFilters([]);
  // }, [resetFilters, setAllFilters]);

  // FIX 2: Extract URL sort without triggering state updates
  // This reads sort parameters directly from URL for display purposes only
  // Solves: Infinite loop caused by setSortBy() calls in useEffect
  const getCurrentSortFromURL = () => {
    const sp = new URLSearchParams(location.search);
    const sortParam = sp.get('sort');
    if (sortParam) {
      const [field, direction] = sortParam.split(',');
      return { field, desc: direction === 'DESC' };
    }
    return null;
  };
  const urlSort = getCurrentSortFromURL();

  // FIX 3: Smart sort state management to prevent duplicate arrows
  // Priority: Manual user sorting > URL sort (only if no user interaction)
  // Solves: Multiple columns showing sort arrows simultaneously
  const getSortState = (column) => {
    // Prioritize react-table sort state (for manual user clicks) over URL sort
    if (column.isSorted) {
      return { active: true, desc: column.isSortedDesc };
    }
    // Only show URL sort if user hasn't manually interacted with sorting
    // This prevents old URL sort from reappearing when user clears manual sort
    if (urlSort && urlSort.field === column.paramKey && !sortBy.length && !hasUserInteracted.current) {
      return { active: true, desc: urlSort.desc };
    }
    return { active: false, desc: false };
  };

  // FIX 4: CRITICAL - This useEffect was causing infinite loops
  // ORIGINAL PROBLEMATIC CODE (DO NOT UNCOMMENT):
  // The following useEffect created circular dependency when URL had sort parameters
  // URL sort → setSortBy() → sortBy change → onSorting() → parent setHeaders() → re-render → loop
  //
  // useEffect(() => {
  //   const sp = new URLSearchParams(location.search);
  //   const sortArr = sp.get('sort')?.split(',');
  //   if (sp.get('sort')) {
  //     const param = columns.find(col => col.paramKey === sortArr[0]);
  //     setSortBy([{ id: param?.accessor, desc: sortArr[1] === 'DESC' }]);
  //   }
  // }, [location.search]);
  //
  // SOLUTION: Removed setSortBy() calls and use direct URL reading instead

  // FIX 5: Track user interaction to manage sort arrow display
  // Mark when user manually interacts with sorting to prevent URL sort conflicts
  useEffect(() => {
    if (serverPaginationProps)
      if (sortBy.length) {
        const param = columns.find(col => col.accessor === sortBy[0].id)
        hasUserInteracted.current = true; // Mark that user has interacted with sorting
        onSorting(param?.paramKey, sortBy[0].desc);
      }
      else {
        if (ref.current) {
          hasUserInteracted.current = true; // Mark interaction even when clearing sort
          onSorting('', '');
        }
        ref.current = true
      }
  }, [sortBy]);

  const onReset = (ev) => {
    ev.stopPropagation();
    const paramsToRetain = ['cid', 'sales_person', 'sid', 'archive', 'include_invoice_status', 'include_archive', 'include_cancelled', 'include_waiting_approval', 'is_supplier', 'filter', "primary","back_order","reorder","actionableOnly","view_type"]; // List of params to retain

    // Parse the current query string
    const currentParams = new URLSearchParams(window.location.search);

    // Create a new query string retaining only specified parameters
    const retainedParams = new URLSearchParams();
    paramsToRetain.forEach((param) => {
      const value = currentParams.get(param);
      if (value) retainedParams.append(param, value); // Add parameter if it exists
    });

    // Construct the new URL and navigate
    const newUrl = `${location.pathname}?${retainedParams.toString()}`;
    navigate(newUrl, { replace: true });

    // FIX 6: Clear react-table sort state when resetting filters
    // This ensures sort arrows disappear when sort parameter is removed from URL
    // Solves: Sort arrows remaining visible after reset when sort param removed
    setSortBy([]);

    // Reset user interaction flag to allow URL sort to work again after reset
    hasUserInteracted.current = false;

    if(resetFunction) {
      dispatch(resetFunction(true));
    } else  {
      // Default Dispatch the reset action
      dispatch(setResetFilters(true));
    }

    if(onParentFilterReset) {
      onParentFilterReset();
    }

  };

  // if (loaderProps?.show)
  //   return <ListInfoMessage loading={loaderProps.loading} message={loaderProps.message} />

  // Render the UI for your table
  return (
    <Paper elevation={elevation} sx={{ width: '100%', overflow: 'hidden', padding: '10px' }}>
      {resetFilters && <Toolbar className={clsx(classes.root, 'flex items-start justify-end')}>
        <div className="flex flex-row items-center">
          <Button
            color="secondary"
            size="small"
            variant="contained"
            sx={{ padding: '0px 15px', marginLeft: '10px' }}
            onClick={(ev) => {
              onReset(ev)
            }}
          >
            Reset Filters
          </Button>
        </div>
      </Toolbar>}
      {toolbarParam && (
        <AuraTableTopToolBar
          toggleAllRowsSelected={toggleAllRowsSelected}
          selectedFlatRows={selectedFlatRows}
          selectedRowIds={selectedRowIds}
          toolbarParam={toolbarParam}
        />
      )}
      <TableContainer>
        <Table {...getTableProps()} stickyHeader>
          <TableHead>
            {headerGroups.map((headerGroup) => {
              const { key: headerGroupKey, rest: headerGroupProps } = splitTableProps(
                headerGroup.getHeaderGroupProps()
              );
              return (
              <TableRow key={headerGroupKey} {...headerGroupProps}>
                {headerGroup.headers.map((column) => {
                  const { key: headerKey, rest: headerProps } = splitTableProps(
                    !column.sortable
                      ? column.getHeaderProps()
                      : column.getHeaderProps(column.getSortByToggleProps())
                  );
                  return (
                  <TableCell
                    key={headerKey}
                    className={clsx(
                      'whitespace-nowrap font-semibold bg-white',
                      column.disablePadding ? 'p-0' : 'px-4 pt-0 pb-8',
                      column.className,
                      column.headerClassName,
                    )}
                    sx={column.headerSxStyle}
                    {...headerProps}
                  >
                    {column.render('Header')}
                    {column.sortable ? (
                      <TableSortLabel
                        active={getSortState(column).active}
                        direction={getSortState(column).desc ? 'desc' : 'asc'}
                      />
                    ) : null}
                    {column.showFilter ? column.render('Filter') : null}
                  </TableCell>
                  );
                })}
              </TableRow>
              );
            })}
          </TableHead>
          <TableBody>
            {loaderWithSpace ?
              (!loading && rows?.length === 0 && page?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={headerGroups[0].headers.length} sx={{ position: 'relative' }}>
                    {!loading && showListInfo &&
                      <div className="loader-overlay" style={{ height: '30vh', width: '100%' }}>
                        <ListInfoMessage loading={false} message={noDataMessage || "No data available!"} />
                      </div>
                    }
                    {!showListInfo &&
                      <div className="loader-overlay" style={{ height: '30vh', width: '100%' }} />
                    }
                  </TableCell>
                </TableRow>
              )
              ) :
              (!loading && showListInfo && rows?.length === 0 && page?.length === 0 && (
                <TableRow>
                  <TableCell colSpan={headerGroups[0].headers.length} sx={{ position: 'relative' }}>
                    <div className="loader-overlay" style={{ height: '30vh', width: '100%' }}>
                      <ListInfoMessage loading={false} message="No data available!" />
                    </div>
                  </TableCell>
                </TableRow>
              ))
            }
            {(loading ? (
              <TableRow>
                <TableCell colSpan={headerGroups[0].headers.length} sx={{ position: 'relative' }}>
                  <div className="loader-overlay">
                    <Loader style={{ height: '30vh', width: '100%' }} />
                  </div>
                </TableCell>
              </TableRow>
            ) : (serverPaginationProps ? rows : page).map((row) => {
              prepareRow(row);
              const { key: rowKey, rest: rowProps } = splitTableProps(row.getRowProps());
              return (
                <TableRow
                  key={rowKey}
                  {...rowProps}
                  onClick={(ev) => {
                    if (onRowClick) onRowClick(ev, row);
                  }}
                  className={`truncate ${onRowClick ? 'cursor-pointer' : 'cursor-default'}`}
                  hover
                >
                  {row.cells.map((cell) => {
                    const { key: cellKey, rest: cellProps } = splitTableProps(cell.getCellProps());
                    return (
                    <TableCell
                      key={cellKey}
                      {...cellProps}
                      className={clsx(
                        cell.column.disablePadding ? 'p-0' : 'px-4 py-6 cu',
                        cell.column.className
                      )}
                      sx={cell.column.sxStyle}
                    >
                      <div>{cell.render('Cell')}</div>
                    </TableCell>
                    );
                  })}
                </TableRow>
              );
            }))}
          </TableBody>
        </Table>
      </TableContainer>
      {showPagination && (
        <TablePagination
          component="div"
          classes={{
            root: 'shrink-0 border-t-1',
          }}
          rowsPerPageOptions={[
            5,
            10,
            25,
            // includeAllOptions ? { label: 'All', value: serverPaginationProps ? count : rows.length + 1 } : 50,
          ]}
          colSpan={5}
          count={count}
          rowsPerPage={rowsPerPage}
          page={currentPage}
          SelectProps={{
            inputProps: { 'aria-label': 'rows per page' },
            native: false,
          }}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          ActionsComponent={AuraTablePaginationActions}
        />
      )}
    </Paper>
  );
};

AuraTable.propTypes = {
  columns: PropTypes.array.isRequired,
  data: PropTypes.array.isRequired,
  onRowClick: PropTypes.func,
};

export default AuraTable;
